import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { MessageSquare, Send, Inbox, Mail, Clock, ChevronRight, User } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Messages = () => {
  const { token, user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [folder, setFolder] = useState('inbox');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMessages();
  }, [token, folder]);

  const fetchMessages = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/messages?folder=${folder}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (messageId) => {
    try {
      await axios.put(`${API}/messages/${messageId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchMessages();
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const handleSelectMessage = (message) => {
    setSelectedMessage(message);
    if (!message.read && folder === 'inbox') {
      markAsRead(message.message_id);
    }
  };

  return (
    <div className="space-y-6" data-testid="messages">
      <div>
        <h1 className="text-2xl font-bold text-neutral-900" style={{ fontFamily: 'Outfit' }}>Spinbox</h1>
        <p className="text-neutral-600 mt-1">Your messages from sponsors and homeowners</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Message List */}
        <div className="lg:col-span-1 bg-white rounded-2xl border border-neutral-200 overflow-hidden">
          <Tabs value={folder} onValueChange={setFolder} className="w-full">
            <TabsList className="w-full grid grid-cols-2 p-1 bg-neutral-100 m-2 rounded-xl">
              <TabsTrigger value="inbox" className="rounded-lg">
                <Inbox className="w-4 h-4 mr-2" />
                Inbox
              </TabsTrigger>
              <TabsTrigger value="sent" className="rounded-lg">
                <Send className="w-4 h-4 mr-2" />
                Sent
              </TabsTrigger>
            </TabsList>

            <div className="max-h-[500px] overflow-y-auto">
              {loading ? (
                <div className="p-4 space-y-3">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="h-16 bg-neutral-100 rounded-xl animate-pulse" />
                  ))}
                </div>
              ) : messages.length > 0 ? (
                <div className="divide-y divide-neutral-100">
                  {messages.map((message) => (
                    <button
                      key={message.message_id}
                      onClick={() => handleSelectMessage(message)}
                      className={`w-full p-4 text-left hover:bg-neutral-50 transition-colors ${
                        selectedMessage?.message_id === message.message_id ? 'bg-neutral-50' : ''
                      } ${!message.read && folder === 'inbox' ? 'bg-blue-50/50' : ''}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-neutral-200 flex items-center justify-center text-sm font-medium text-neutral-600">
                          {folder === 'inbox' 
                            ? message.sender?.name?.charAt(0).toUpperCase() 
                            : message.receiver?.name?.charAt(0).toUpperCase()
                          }
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <p className={`font-medium truncate ${!message.read && folder === 'inbox' ? 'text-neutral-900' : 'text-neutral-700'}`}>
                              {folder === 'inbox' ? message.sender?.name : message.receiver?.name}
                            </p>
                            {!message.read && folder === 'inbox' && (
                              <span className="w-2 h-2 bg-blue-500 rounded-full" />
                            )}
                          </div>
                          <p className="text-sm text-neutral-900 font-medium truncate">{message.subject}</p>
                          <p className="text-xs text-neutral-500 mt-1">
                            {new Date(message.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="p-8 text-center">
                  <Mail className="w-12 h-12 text-neutral-300 mx-auto mb-3" />
                  <p className="text-neutral-500">No messages</p>
                </div>
              )}
            </div>
          </Tabs>
        </div>

        {/* Message Detail */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-neutral-200 p-6">
          {selectedMessage ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              key={selectedMessage.message_id}
            >
              <div className="flex items-start justify-between mb-6 pb-4 border-b border-neutral-100">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-neutral-200 flex items-center justify-center text-lg font-medium text-neutral-600">
                    {folder === 'inbox' 
                      ? selectedMessage.sender?.name?.charAt(0).toUpperCase() 
                      : selectedMessage.receiver?.name?.charAt(0).toUpperCase()
                    }
                  </div>
                  <div>
                    <p className="font-semibold text-neutral-900">
                      {folder === 'inbox' ? selectedMessage.sender?.name : selectedMessage.receiver?.name}
                    </p>
                    <p className="text-sm text-neutral-500">
                      {folder === 'inbox' ? selectedMessage.sender?.email : selectedMessage.receiver?.email}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-sm text-neutral-500">
                  <Clock className="w-4 h-4" />
                  {new Date(selectedMessage.created_at).toLocaleString()}
                </div>
              </div>

              <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Outfit' }}>
                {selectedMessage.subject}
              </h2>

              <div className="prose prose-neutral max-w-none">
                <p className="text-neutral-600 whitespace-pre-wrap">{selectedMessage.content}</p>
              </div>
            </motion.div>
          ) : (
            <div className="h-full flex items-center justify-center text-center">
              <div>
                <MessageSquare className="w-16 h-16 text-neutral-200 mx-auto mb-4" />
                <p className="text-neutral-500">Select a message to read</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Messages;
